/**
 * mSTEM EXT-02 block builder modal container (Phase 6 / Plan 06-03,
 * MOCK-ONLY / DR-02). Holds the builder config state and wires:
 *   - live config edits (name / shape / color / add-remove inputs) → the
 *     presentational component re-renders the live SVG preview every change.
 *   - "Test trong workspace" → reveal the sandbox preview + fire the
 *     extBlockTestAdded toast. It NEVER touches the real block engine — the
 *     preview is the pure-SVG mock (Pitfall 1 / threat T-06-03-02).
 *   - "Lưu khối" → validate name non-empty, persist to mstem.customBlocks via
 *     addCustomBlock, fire the extBlockSaved toast, reset, and close.
 *
 * React 16 class component (no hooks); handlers bound via lodash.bindall.
 */
import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import VM from 'openblock-vm';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {injectIntl} from 'react-intl';

import {closeBlockBuilder, setExtBlockEditEntry} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';
import {addCustomBlock} from '../lib/mstem-extension-storage.js';
import {generateId} from '../lib/mstem-share-link.js';
import {DEFAULT_SWATCH} from '../lib/mstem-mock/ext-block-types.js';
import {registerOrRefreshCustomBlocks} from '../lib/mstem-custom-blocks-extension.js';

import BlockBuilderModalComponent from '../components/block-builder-modal/block-builder-modal.jsx';

const emptyState = () => ({
    name: '',
    shape: 'command',
    color: DEFAULT_SWATCH,
    inputs: [],
    showSandbox: false,
    nameError: null
});

// UC-47 S-2 edit mode — seed the builder from an existing mstem.customBlocks
// entry so "Sửa" opens prefilled. editId is retained so handleSave updates the
// same record (upsert-by-id) instead of creating a duplicate.
const stateFromEntry = entry => ({
    name: entry.name || '',
    shape: entry.shape || 'command',
    color: entry.color || DEFAULT_SWATCH,
    inputs: Array.isArray(entry.inputs) ? entry.inputs : [],
    showSandbox: false,
    nameError: null
});

class BlockBuilderModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleName',
            'handleShape',
            'handleColor',
            'handleAddInput',
            'handleRemoveInput',
            'handleInputName',
            'handleInputType',
            'handleTest',
            'handleSave',
            'handleRequestClose'
        ]);
        // editId !== null → UC-47 S-2 edit mode (update-by-id on save).
        this.editId = (props.editEntry && props.editEntry.id) || null;
        this.state = props.editEntry ? stateFromEntry(props.editEntry) : emptyState();
    }
    handleName (e) {
        const name = e.target.value;
        this.setState({name, nameError: name.trim() ? null : this.state.nameError});
    }
    handleShape (e) {
        this.setState({shape: e.target.value});
    }
    handleColor (e) {
        // swatch buttons carry data-color; the native <input type=color> uses value
        const color = (e.currentTarget && e.currentTarget.dataset && e.currentTarget.dataset.color) ||
            e.target.value;
        this.setState({color});
    }
    handleAddInput () {
        this.setState(prev => ({
            inputs: prev.inputs.concat({name: '', type: 'number'})
        }));
    }
    handleRemoveInput (arg) {
        const idx = typeof arg === 'number' ?
            arg :
            Number(arg.currentTarget.dataset.index);
        this.setState(prev => ({
            inputs: prev.inputs.filter((input, i) => i !== idx)
        }));
    }
    handleInputName (e) {
        const idx = Number(e.currentTarget.dataset.index);
        const value = e.target.value;
        this.setState(prev => ({
            inputs: prev.inputs.map((input, i) => (i === idx ? Object.assign({}, input, {name: value}) : input))
        }));
    }
    handleInputType (e) {
        const idx = Number(e.currentTarget.dataset.index);
        const value = e.target.value;
        this.setState(prev => ({
            inputs: prev.inputs.map((input, i) => (i === idx ? Object.assign({}, input, {type: value}) : input))
        }));
    }
    handleTest () {
        // Mock-only: reveal the sandbox preview + toast. No real block injection.
        this.setState({showSandbox: true});
        showAlertWithTimeout(this.props.dispatch, 'extBlockTestAdded');
    }
    handleSave () {
        const name = (this.state.name || '').trim();
        if (!name) {
            this.setState({nameError: 'name'});
            return;
        }
        const isEdit = Boolean(this.editId);
        addCustomBlock({
            // Reuse the existing id when editing → addCustomBlock upserts by id
            // (UC-47 S-2: update the same record, never duplicate).
            id: this.editId || `block-${generateId()}`,
            name: name,
            shape: this.state.shape,
            color: this.state.color,
            inputs: this.state.inputs
        });
        // Bridge the saved block into the VM so it appears in the palette
        // immediately (register on first block, refresh on subsequent ones).
        registerOrRefreshCustomBlocks(this.props.vm);
        showAlertWithTimeout(this.props.dispatch, isEdit ? 'extBlockUpdated' : 'extBlockSaved');
        this.resetAndClose();
    }
    resetAndClose () {
        this.editId = null;
        this.props.onClearEditEntry();
        this.setState(emptyState());
        this.props.onClose();
    }
    handleRequestClose () {
        this.resetAndClose();
    }
    render () {
        return (
            <BlockBuilderModalComponent
                color={this.state.color}
                inputs={this.state.inputs}
                name={this.state.name}
                nameError={this.state.nameError}
                shape={this.state.shape}
                showSandbox={this.state.showSandbox}
                onAddInput={this.handleAddInput}
                onColor={this.handleColor}
                onInputName={this.handleInputName}
                onInputType={this.handleInputType}
                onName={this.handleName}
                onRemoveInput={this.handleRemoveInput}
                onRequestClose={this.handleRequestClose}
                onSave={this.handleSave}
                onShape={this.handleShape}
                onTest={this.handleTest}
            />
        );
    }
}

BlockBuilderModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    editEntry: PropTypes.shape({id: PropTypes.string}), // null = create mode
    onClearEditEntry: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM).isRequired
};

const mapStateToProps = state => ({
    editEntry: state.scratchGui.modals.extBlockEditEntry,
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onClose: () => dispatch(closeBlockBuilder()),
    onClearEditEntry: () => dispatch(setExtBlockEditEntry(null))
});

export default compose(
    injectIntl,
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(BlockBuilderModal);
